import React, { useEffect, useState } from "react";
import { Modal, ModalProps } from "react-bootstrap";
import { RoleType } from "store/types";
import { useRolesDispatch } from "store/hooks/roles";

import "./RoleModal.scss";

type RoleModalProps = ModalProps & {
  onClose: () => void;
  selectedRole?: RoleType;
};

const emptyRole: RoleType = {
  _id: "",
  role: "",
  datasets: [],
  permissions: [],
};

const hasValue = (value: string): boolean => value.trim().length > 0;

const hasDatasetValue = (datasets: string[]): boolean =>
  datasets.some((dataset) =>
    dataset.split(",").some((value) => hasValue(value))
  );

const normalizeRole = (role: RoleType): RoleType => ({
  ...role,
  role: role.role.trim(),
  datasets: role.datasets
    .map((dataset) =>
      dataset
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
        .join(",")
    )
    .filter(Boolean),
  permissions: role.permissions
    .map((permission) => permission.trim())
    .filter(Boolean),
});

const RoleModal: React.FC<RoleModalProps> = ({
  onClose,
  selectedRole,
  ...props
}) => {
  const { createRole, updateRole } = useRolesDispatch();
  const [role, setRole] = useState<RoleType>(
    selectedRole ? { ...selectedRole } : emptyRole
  );

  useEffect(() => {
    setRole(selectedRole ? { ...selectedRole } : emptyRole);
  }, [selectedRole]);

  const updateRoleField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setRole((prev) => ({ ...prev, role: value }));
  };

  const updateDatasetsField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setRole((prev) => ({ ...prev, datasets: [value] }));
  };

  const updatePermissionsField = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setRole((prev) => ({ ...prev, permissions: value.split("\n") }));
  };

  const isSaveDisabled =
    !hasValue(role.role) || !hasDatasetValue(role.datasets);

  const handleSave = () => {
    if (isSaveDisabled) return;

    const normalizedRole = normalizeRole(role);
    if (selectedRole) {
      updateRole({ ...normalizedRole, _id: selectedRole._id });
    } else {
      createRole(normalizedRole);
    }
    onClose();
  };

  return (
    <Modal
      {...props}
      animation={false}
      onHide={onClose}
      dialogClassName={"role-modal"}
      contentClassName={"role-modal-content"}
    >
      <Modal.Header closeButton={true} className={"role-modal-header"}>
        <div className={"title"}>{selectedRole ? "Edit Role" : "Add Role"}</div>
      </Modal.Header>
      <Modal.Body className={"role-modal-body"}>
        <div className={"role-modal-field"}>
          <div className={"material-textbox"}>
            <input
              className={"material-input"}
              required={true}
              type={"text"}
              value={role.role}
              onChange={updateRoleField}
            />
            <span className={"material-label"}>Role Name</span>
          </div>
        </div>
        <div className={"role-modal-field"}>
          <div className={"material-textbox"}>
            <input
              className={"material-input"}
              required={true}
              type={"text"}
              value={role.datasets.join(",")}
              onChange={updateDatasetsField}
            />
            <span className={"material-label"}>
              Datasets{" "}
              <small className={"role-modal-helper"}>
                Separate values Team IDs using commas{" "}
                <span className={"role-modal-red"}>(,)</span> e.g. ({" "}
                <strong>3700,9600</strong> )
              </small>
            </span>
          </div>
        </div>

        <div className={"role-modal-field"}>
          <div className={"role-modal-field-label"}>
            Permissions <span>One per line. Follow the example below.</span>
          </div>
          <div className={"role-modal-permission-example"}>
            <span className={"permission-key"}>League</span>
            <span className={"pill"}>ncaa</span>
            <span className={"permission-key"}>Type</span>
            <span className={"pill"}>practice</span>
            <span className={"permission-key"}>Team ID</span>
            <span className={"pill"}>194</span>
            <span className={"permission-key"}>*</span>
            <span className={"pill"}>*</span>
            <span className={"permission-key"}>Permission</span>
            <span className={"pill"}>w</span>
          </div>
          <div className={"role-modal-permission-hint"}>
            Separate values using dots{" "}
            <span className={"role-modal-red"}>(.)</span> e.g. ({" "}
            <strong>ncaa-fbs.*.194.*.w</strong> )
          </div>
          <textarea
            className={"role-modal-textarea"}
            value={role.permissions.join("\n")}
            onChange={updatePermissionsField}
            rows={5}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className={"text-right footer-s"}>
        <button className={"btn-transparet"} onClick={onClose}>
          Cancel
        </button>
        <button
          className={"btn btn-blue"}
          disabled={isSaveDisabled}
          style={isSaveDisabled ? { opacity: 0.6 } : {}}
          onClick={handleSave}
        >
          {selectedRole ? "Save" : "Create"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default RoleModal;
